import React from "react";

const PRINCIPLES = [
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3">
        <path d="M3 21h18M5 21V9l7-5 7 5v12M9 21v-8h6v8" />
      </svg>
    ),
    title: "High-Performance Envelope",
    desc: "Advanced framing, dense-pack insulation, and meticulous air-sealing — homes that hold their temperature and their value.",
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3">
        <path d="M12 2v4M12 18v4M4 12H2M22 12h-2M19.07 4.93l-1.41 1.41M6.34 17.66l-1.41 1.41M19.07 19.07l-1.41-1.41M6.34 6.34L4.93 4.93" />
        <circle cx="12" cy="12" r="4" />
      </svg>
    ),
    title: "Efficient HVAC & Heat Pumps",
    desc: "High-SEER heat pumps, zoned mini-splits, and ERV fresh-air systems deliver quiet comfort at a fraction of legacy energy loads.",
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3">
        <path d="M12 2C8 6 5 10 5 14a7 7 0 0014 0c0-4-3-8-7-12z" />
        <path d="M12 22V10M9 13l3-3 3 3" />
      </svg>
    ),
    title: "Sustainable Materials",
    desc: "FSC-certified lumber, reclaimed accents, recycled-content products, and regional sourcing — craft that respects where it comes from.",
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3">
        <path d="M12 2v6M12 22a7 7 0 007-7c0-5-7-13-7-13S5 10 5 15a7 7 0 007 7z" />
      </svg>
    ),
    title: "Water Conservation",
    desc: "Low-flow fixtures, drip-irrigation, drought-tolerant landscaping, and rainwater-capture plumbing roughed in from day one.",
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3">
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
      </svg>
    ),
    title: "Solar-Ready Design",
    desc: "Roof orientation, reinforced framing, and pre-wired conduits let you add solar and battery storage today — or fifteen years from now.",
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3">
        <path d="M3 12h18M3 6h18M3 18h18M7 6v12M17 6v12" />
      </svg>
    ),
    title: "Healthy Indoor Air",
    desc: "Zero-VOC finishes, HEPA-grade filtration, and balanced mechanical ventilation — homes that feel as clean as they look.",
  },
];

const STATS = [
  { value: "30%+", label: "Lower Energy Use vs. Code Minimum" },
  { value: "ZERO", label: "VOC Finishes Standard" },
  { value: "100%", label: "Projects Solar-Ready" },
];

export default function GreenBuilding() {
  return (
    <section
      id="green"
      data-testid="green-building-section"
      className="relative py-28 md:py-40 overflow-hidden"
    >
      {/* Subtle sage-green accent flourish */}
      <div
        className="absolute -bottom-40 -left-40 w-[700px] h-[700px] rounded-full pointer-events-none blur-3xl"
        style={{ background: "rgba(142, 168, 154, 0.06)" }}
      />
      <div
        className="absolute -top-40 right-0 w-[500px] h-[500px] rounded-full pointer-events-none blur-3xl"
        style={{ background: "rgba(224, 192, 151, 0.04)" }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 md:gap-16 mb-20">
          {/* Left: heading */}
          <div className="lg:col-span-5 reveal">
            <div className="flex items-center gap-4 mb-6">
              <span
                className="w-10 h-[1px]"
                style={{
                  background:
                    "linear-gradient(90deg, transparent, rgba(142,168,154,0.7), transparent)",
                }}
              />
              <span
                className="font-body text-[11px] uppercase tracking-[0.3em] font-medium"
                style={{ color: "#8ea89a" }}
              >
                Green Building
              </span>
            </div>

            <h2 className="font-display text-5xl md:text-6xl leading-[1.02] tracking-tight text-[var(--text-primary)] mb-8">
              Built to last.
              <br />
              <em className="text-[var(--accent)]">Built to tread lightly.</em>
            </h2>

            <p className="text-[var(--text-secondary)] text-lg leading-relaxed mb-10 max-w-md">
              Sustainability isn't a checkbox we add at the end — it's baked
              into the framing, the envelope, the mechanicals, and the
              materials. The result: homes that cost less to run, feel better
              to live in, and hold their value for generations.
            </p>

            <div className="grid grid-cols-3 gap-4 pt-8 border-t border-white/10">
              {STATS.map((s) => (
                <div key={s.label} data-testid={`green-stat-${s.value}`}>
                  <div
                    className="font-display text-3xl md:text-4xl leading-none mb-3"
                    style={{ color: "#b7cdc0" }}
                  >
                    {s.value}
                  </div>
                  <div className="font-body text-[10px] uppercase tracking-[0.25em] text-[var(--text-secondary)] leading-snug">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: principles grid */}
          <div className="lg:col-span-7 grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {PRINCIPLES.map((p, i) => (
              <article
                key={p.title}
                className="reveal group relative p-8 md:p-9 bg-[var(--bg-primary)] transition-colors duration-500 hover:bg-[var(--bg-secondary)]"
                style={{ transitionDelay: `${(i % 3) * 80}ms` }}
                data-testid={`green-principle-${i + 1}`}
              >
                <div
                  className="mb-6 transition-colors duration-500"
                  style={{ color: "#8ea89a" }}
                >
                  {p.icon}
                </div>
                <h3 className="font-display text-xl md:text-2xl text-[var(--text-primary)] mb-3 leading-tight">
                  {p.title}
                </h3>
                <p className="text-[var(--text-secondary)] leading-relaxed text-[14px]">
                  {p.desc}
                </p>
              </article>
            ))}
          </div>
        </div>

        {/* Footer strip — certifications awareness */}
        <div
          className="reveal pt-10 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-6"
          data-testid="green-cert-strip"
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <a
              href="https://www.azimuthrenew.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 w-fit bg-white rounded-lg px-3 py-2 shadow-lg hover:scale-105 transition-transform duration-300"
              data-testid="azimuth-logo-green"
              aria-label="Azimuth Renewable Energy"
            >
              <img
                src="/azimuth-logo.png"
                alt="Azimuth Renewable Energy logo"
                className="h-24 w-auto object-contain"
                loading="lazy"
              />
            </a>
            <p className="font-body text-sm text-[var(--text-secondary)] leading-relaxed max-w-2xl">
            We build to the spirit of{" "}
            <span className="text-[var(--text-primary)]">ENERGY STAR®</span>,{" "}
            <span className="text-[var(--text-primary)]">LEED</span>, and{" "}
            <span className="text-[var(--text-primary)]">Passive House</span>{" "}
            principles — pursuing formal certification whenever a client's
            goals call for it. For solar and renewable energy projects, we
            work alongside our partner{" "}
            <a
              href="https://www.azimuthrenew.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--accent)] underline underline-offset-4 decoration-[var(--accent)]/40 hover:decoration-[var(--accent)] transition-colors"
              data-testid="azimuth-link-green"
            >
              Azimuth Renewable Energy
            </a>
            .
          </p>
          </div>
          <a
            href="#contact"
            className="btn-outline w-fit"
            data-testid="green-building-cta"
            style={{ borderColor: "#8ea89a", color: "#b7cdc0" }}
          >
            Build Greener With Us
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
            >
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}
